import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Alert,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PauseCircleOutline as SuspendIcon,
  WarningAmber as WarningIcon,
  People as UsersIcon,
  Visibility as ImpersonateIcon,
  AdminPanelSettings as AdminIcon,
  MailOutline as MailIcon,
} from '@mui/icons-material';
import { useLanguage } from '../contexts/LanguageContext';
import { useFormatageDates } from '../utils/dates';
import EnteteEcran from '../components/commun/EnteteEcran';
import DialogueConfirmation from '../components/commun/DialogueConfirmation';
import DialogueSuppressionEntreprise from '../components/commun/DialogueSuppressionEntreprise';
import {
  getCompanies,
  getCompany,
  createCompany,
  inviteCompanyAdmin,
  updateCompany,
  deleteCompany,
  getImpactSuppression,
  impersonate,
  getImpersonationLogs,
  designerAdmin,
} from '../services/superAdminService';
import { stockQuotaFromCompanyRow, SELECTABLE_PLANS } from '../utils/quotas';

/**
 * Console super-admin (D-UI-1) — entreprises : liste, création, édition.
 *
 * Le SUPER_ADMIN a `company_id` NULL et AUCUN stock : il ne peut pas utiliser les
 * écrans métier, tous scopés par `X-Stock-ID`. Cette console est son seul point
 * d'entrée. Elle est montée par SuperAdminShell, au-dessus de la garde
 * `!currentStock` de App.js.
 *
 * UI-6 : impersonation et journal d'audit. La suppression arrive au lot suivant.
 */

/** Champ vide -> null (RETRAIT de l'override). Tester la CHAÎNE avant de convertir :
 *  Number('') vaut 0, pas null — et `Number(v) || null` écraserait aussi un 0 saisi. */
const toOverride = (raw) => (raw === '' ? null : Number(raw));

/** Override serveur (null quand l'entreprise suit son plan) -> valeur du champ. */
const fromOverride = (value) => (value === null || value === undefined ? '' : String(value));

const EMPTY_FORM = { name: '', plan: 'basic', status: 'active', maxStocks: '', usersPerStock: '', adminEmail: '' };

const SuperAdminConsole = ({ showSnackbar }) => {
  const { t } = useLanguage();
  const { formaterHorodatage } = useFormatageDates();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  // Refus de suppression (409) : { company, message }. Un window.confirm ne sait offrir
  // qu'OK/Annuler — il ne peut pas proposer « Suspendre à la place ». D'où un Dialog.
  const [deleteBlocked, setDeleteBlocked] = useState(null);

  // Vue de détail (utilisateurs d'une entreprise). Motif `viewMode` d'OrderReception.js :
  // la vue REMPLACE la liste plutôt que de s'y empiler. Cohérent avec la forme du
  // payload — getCompany est une route de DÉTAIL (company + stocks[] + users[]).
  const [detail, setDetail] = useState(null);
  /**
   * L'utilisateur qu'on s'apprête à désigner ADMIN, ou `null`.
   *
   * 🔴 UN ÉTAT, PAS UN `window.confirm`. Le geste change le rôle de DEUX
   * personnes : le désigné devient ADMIN, l'ADMIN en place redevient USER. *Un
   * dialogue natif ne sait pas dire qui perd sa place* — et c'est justement ce
   * qu'il faut lire avant de cliquer.
   */
  /**
   * 🔴 LES TROIS DERNIERS `window.confirm` DE CET ÉCRAN DEVIENNENT DES ANNONCES.
   * Supprimer une entreprise, la suspendre, prendre l'identité de quelqu'un :
   * trois gestes lourds derrière un dialogue natif qui ne montrait RIEN.
   * *« Supprimer cette entreprise » n'apprend rien ; « elle porte 3 sites et
   * 7 utilisateurs » décide.*
   * ⚠️ `aConfirmer` porte le TYPE et l'ENTITÉ — l'annonce a besoin des deux
   * pour dire ce que le geste emporte.
   */
  // 🔴 La suppression a SON PROPRE état : elle porte un impact chargé depuis
  // le serveur et une erreur qui doit rester DANS le dialogue.
  const [suppression, setSuppression] = useState(null);
  const [impactEnCours, setImpactEnCours] = useState(false);
  const [aConfirmer, setAConfirmer] = useState(null);
  const [confirmationEnCours, setConfirmationEnCours] = useState(false);

  const [designation, setDesignation] = useState(null);
  const [designationEnCours, setDesignationEnCours] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  // Onglets : « Entreprises » et « Journal » sont des sections PAIRES, sans hiérarchie —
  // le journal est global, pas le forage d'une entreprise. <Tabs> est déjà l'idiome du
  // projet pour ce cas (Admin.js : Utilisateurs / Stocks).
  // Le viewMode de la vue de détail reste CONFINÉ à l'onglet Entreprises : il encode un
  // FORAGE (on descend dans une ligne, on remonte par « Retour »), pas une bascule
  // latérale. Les mélanger donnerait un bouton « Retour » qui signifie deux choses
  // différentes selon l'endroit.
  const [tab, setTab] = useState(0);

  const [logs, setLogs] = useState([]);
  const [logsTotal, setLogsTotal] = useState(0);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsError, setLogsError] = useState(null);

  const loadCompanies = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Un seul appel : nb_admins arrive dans la réponse (companies.py::_usage). La
      // première version dérivait les rôles depuis GET /auth/users, que
      // scope_to_company renvoie EN ENTIER pour un SUPER_ADMIN — soit toute la table
      // users, non paginée et avec les stocks imbriqués, pour afficher une pastille.
      // ~1,2 Ko par utilisateur : le coût croissait au rythme du succès du produit.
      const data = await getCompanies();
      setCompanies(Array.isArray(data?.companies) ? data.companies : []);
    } catch (err) {
      // Message du serveur s'il existe, sinon le message technique : on n'invente pas
      // de texte générique qui masquerait la cause réelle.
      setError(err?.response?.data?.error || err?.message || t('superAdmin.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  const handleRetry = () => {
    loadCompanies();
    if (showSnackbar) {
      showSnackbar(t('superAdmin.retrying'), 'info');
    }
  };

  const loadLogs = useCallback(async () => {
    setLogsLoading(true);
    setLogsError(null);
    try {
      const data = await getImpersonationLogs();
      setLogs(Array.isArray(data?.logs) ? data.logs : []);
      setLogsTotal(typeof data?.total === 'number' ? data.total : 0);
    } catch (err) {
      // Le journal EST le contenu de l'onglet : son échec doit être visible, comme
      // pour la liste d'entreprises. Message serveur d'abord, jamais un générique.
      setLogsError(err?.response?.data?.error || err?.message || t('superAdmin.auditLoadError'));
    } finally {
      setLogsLoading(false);
    }
  }, [t]);

  /**
   * Changement d'onglet.
   *
   * ⚠️ `setDetail(null)` est OBLIGATOIRE. Sans lui, quitter l'onglet Entreprises depuis
   * une vue de détail puis y revenir rouvrirait cette vue — l'utilisateur croyait
   * l'avoir quittée. Le bug ne se manifeste que sur cet enchaînement précis, donc des
   * semaines plus tard et sans explication apparente.
   */
  const handleTabChange = (event, newValue) => {
    setTab(newValue);
    setDetail(null);
    if (newValue === 1) {
      loadLogs();
    }
  };

  /** Ouverture : préremplissage depuis l'entreprise, ou remise à zéro (motif Admin.js). */
  /**
   * Invitation d'un administrateur sur une entreprise EXISTANTE.
   * ⚠️ C'est le CHEMIN PRINCIPAL — le champ email du dialogue de création
   * n'est qu'un raccourci. Sans ce bouton, le raccourci serait le seul chemin,
   * et une entreprise créée sans email resterait sans administrateur.
   */
  /**
   * 🔴 `window.prompt` N'EST PAS UNE CONFIRMATION : C'EST UN FORMULAIRE, ET
   * C'ÉTAIT LE PIRE DES SIX DIALOGUES SYSTÈME.
   *
   * Les cinq `window.confirm` ne montraient rien ; celui-ci **SAISISSAIT UNE
   * ADRESSE E-MAIL** dans un champ système — sans validation de format, sans
   * message d'erreur possible, sans état de chargement, et sans suivre ni le
   * thème ni la langue de l'application. ⚠️ *Un dialogue natif qui prend une
   * SAISIE ne peut rien vérifier avant de rendre la main* : l'adresse partait
   * telle quelle, et le seul retour possible était l'échec du serveur.
   *
   * ⚠️ ET IL BLOQUE LE FIL D'EXÉCUTION : tant qu'il est ouvert, rien ne se
   * rafraîchit — ni la liste, ni un éventuel message d'une autre action.
   */
  const [invitation, setInvitation] = useState(null);   // { company, email }
  const [invitationEnCours, setInvitationEnCours] = useState(false);

  const handleInvite = (company) => setInvitation({ company, email: '' });

  const envoyerInvitation = async () => {
    const company = invitation?.company;
    const propre = (invitation?.email || '').trim();
    if (!company || !propre) return;
    setInvitationEnCours(true);
    try {
      await inviteCompanyAdmin(company.id, propre);
      showSnackbar(t('superAdmin.inviteSent', { email: propre }), 'success');
      loadCompanies();
    } catch (e) {
      showSnackbar(e?.response?.data?.error || t('superAdmin.inviteError'), 'error');
    } finally {
      setInvitationEnCours(false);
      setInvitation(null);
    }
  };

  const handleDialogOpen = (company = null) => {
    if (company) {
      setEditingCompany(company);
      setFormData({
        name: company.name,
        plan: company.plan,
        status: company.status,
        // 🔴 PIÈGE N°1 : on lit `*_override`, JAMAIS `max_stocks` (limite effective).
        // Préremplir depuis la limite effective figerait en override une valeur héritée
        // du plan : l'entreprise cesserait de suivre les changements de plan, sans que
        // rien ne bouge à l'écran.
        maxStocks: fromOverride(company.max_stocks_override),
        usersPerStock: fromOverride(company.users_per_stock_override),
      });
    } else {
      setEditingCompany(null);
      setFormData(EMPTY_FORM);
    }
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      // On envoie l'override sous la clé `max_stocks` : c'est le nom d'ÉCRITURE.
      // `max_stocks_override` serait une clé inconnue, ignorée en silence (200 sans effet).
      const payload = {
        name: formData.name.trim(),
        plan: formData.plan,
        max_stocks: toOverride(formData.maxStocks),
        users_per_stock: toOverride(formData.usersPerStock),
      };

      if (editingCompany) {
        // `status` n'existe qu'en édition : le POST l'ignore et force 'active'.
        payload.status = formData.status;
        await updateCompany(editingCompany.id, payload);
        showSnackbar(t('superAdmin.updated'), 'success');
      } else {
        const cree = await createCompany(payload);
        const email = formData.adminEmail.trim();
        if (!email) {
          showSnackbar(t('superAdmin.created'), 'success');
        } else {
          // ⚠️ DEUX GESTES, DEUX ISSUES. L'entreprise est créée ; si
          // l'invitation échoue, elle EXISTE quand même. Un échec global
          // laisserait croire que rien n'a été fait, et le super-admin
          // recréerait une entreprise en double. Le message dit précisément
          // ce qui a réussi et ce qui reste à faire.
          const id = cree?.company?.id ?? cree?.id;
          try {
            await inviteCompanyAdmin(id, email);
            showSnackbar(t('superAdmin.inviteSent', { email }), 'success');
          } catch (e) {
            showSnackbar(
              e?.response?.data?.error
                ? `${t('superAdmin.inviteFailed')} (${e.response.data.error})`
                : t('superAdmin.inviteFailed'),
              'warning',
            );
          }
        }
      }
      setDialogOpen(false);
      loadCompanies();
    } catch (err) {
      showSnackbar(
        err?.response?.data?.error || err?.message || t('superAdmin.operationError'),
        'error',
      );
    } finally {
      setSaving(false);
    }
  };

  /**
   * Suppression. La confirmation NOMME l'entreprise : on ne supprime pas une entité
   * nommée sans la nommer.
   *
   * ⚠️ Seul le 409 ouvre le dialogue de refus — c'est le seul code qui signale une
   * précondition métier réversible (entreprise non vide), donc le seul où proposer une
   * alternative a un sens. 403 (rôle insuffisant), 404 (déjà supprimée) et 500 partent
   * au snackbar : suggérer « Suspendre » y serait absurde, voire trompeur.
   */
  /**
   * 🔴 OUVRIR L'ANNONCE — ON NE SUPPRIME PLUS DEPUIS UN BOUTON.
   * Le geste se fait en deux temps : demander au SERVEUR ce qu'il détruirait,
   * puis exiger le nom exact. *Le geste le plus destructeur de la plateforme
   * est le seul qui mérite plus qu'un clic.*
   * ⚠️ L'IMPACT EST RECHARGÉ À CHAQUE OUVERTURE, jamais mémorisé : entre deux
   * ouvertures, l'entreprise a pu recevoir des commandes. Un décompte gardé en
   * mémoire annoncerait l'état d'il y a dix minutes.
   */
  const ouvrirSuppression = async (company) => {
    setSuppression({ company, impact: null, erreur: null });
    setImpactEnCours(true);
    try {
      setSuppression({ company, impact: await getImpactSuppression(company.id), erreur: null });
    } catch (err) {
      setSuppression(null);
      showSnackbar(
        err?.response?.data?.error || err?.message || t('superAdmin.operationError'),
        'error',
      );
    } finally {
      setImpactEnCours(false);
    }
  };

  /**
   * ⚠️ LE 409 « VIDEZ-LA D'ABORD » A DISPARU DU SERVEUR, et son traitement
   * avec. Il est remplacé par la cascade : la garde protégeait le bon principe
   * mais visait le mauvais rôle. *Un refus qui demande un travail impraticable
   * n'est pas une protection : c'est une fonction absente avec un message
   * d'erreur.*
   * ⚠️ Le 400 de confirmation reste DANS le dialogue — le fermer effacerait ce
   * que l'utilisateur vient de taper, et l'obligerait à tout reprendre pour
   * une faute de frappe.
   */
  const handleDelete = async (confirmation) => {
    if (!suppression?.company) return;
    setConfirmationEnCours(true);
    try {
      const { bilan } = await deleteCompany(suppression.company.id, confirmation);
      setSuppression(null);
      // 🔴 LE BILAN RÉEL, PAS L'ANNONCE. Ce qui a été supprimé peut différer de
      // ce qui était annoncé — un fichier déjà absent du disque, par exemple.
      // *Rendre compte de ce qu'on a fait, jamais de ce qu'on avait prévu.*
      showSnackbar(
        t('superAdmin.deletedWithCount', {
          name: bilan?.company_name || '',
          files: bilan?.fichiers_supprimes ?? 0,
        }),
        'success',
      );
      loadCompanies();
    } catch (err) {
      setSuppression((s) => (s ? {
        ...s,
        erreur: err?.response?.data?.error || err?.message || t('superAdmin.operationError'),
      } : s));
    } finally {
      setConfirmationEnCours(false);
    }
  };

  /**
   * Suspension. SECONDE confirmation, nommée elle aussi : suspendre COUPE TOUS LES
   * LOGINS de l'entreprise — conséquence plus lourde que la suppression qu'on vient de
   * refuser. Un clic depuis le dialogue de refus ne doit pas suffire.
   *
   * Réutilise updateCompany : le PUT est une mise à jour PARTIELLE, envoyer le seul
   * champ `status` laisse tout le reste inchangé. Pas de méthode de service dédiée.
   * La suspension reste par ailleurs atteignable via le dialogue d'édition (et c'est le
   * seul chemin pour RÉACTIVER) : ce raccourci est une commodité, pas le seul chemin.
   */
  const handleSuspend = async (company) => {
    try {
      await updateCompany(company.id, { status: 'suspended' });
      showSnackbar(t('superAdmin.suspended'), 'success');
      setDeleteBlocked(null);   // fermer AVANT de recharger : sinon le dialogue reste
      loadCompanies();          // affiché avec des compteurs périmés le temps du fetch
    } catch (err) {
      showSnackbar(
        err?.response?.data?.error || err?.message || t('superAdmin.operationError'),
        'error',
      );
    }
  };

  /** Ouvre la vue de détail d'une entreprise (ses utilisateurs). */
  const handleViewUsers = async (company) => {
    setDetailLoading(true);
    try {
      const data = await getCompany(company.id);
      setDetail(data?.company || null);
    } catch (err) {
      showSnackbar(
        err?.response?.data?.error || err?.message || t('superAdmin.operationError'),
        'error',
      );
    } finally {
      setDetailLoading(false);
    }
  };

  /**
   * Démarrer une impersonation.
   *
   * Confirmation NOMMÉE : on endosse l'identité de quelqu'un, ce n'est pas anodin — et
   * tant que le bandeau de retour n'existe pas (lot suivant), c'est un aller SANS ISSUE
   * autre que la déconnexion.
   *
   * ⚠️ LE `window.location.reload()` EST NÉCESSAIRE, NE PAS LE RETIRER.
   * Le serveur a posé des cookies portant l'identité de la CIBLE, mais le state React
   * tient encore celle du SUPER_ADMIN. Seul un rechargement complet fait relire
   * `initializeAuth()` (App.js) depuis localStorage : le rôle devient USER/ADMIN, le
   * bloc `role === 'SUPER_ADMIN'` cesse de s'appliquer, et le châssis MÉTIER est rendu.
   * Sans lui, l'UI afficherait la console du SA avec les droits de la cible.
   *
   * Aucun snackbar : il serait détruit par le rechargement avant d'être lu. Le
   * basculement complet d'interface EST le retour visuel — plus fort qu'un message.
   */
  /**
   * Désigne un ADMIN — et RECHARGE le détail, parce que le geste change DEUX
   * lignes du tableau qu'on regarde.
   *
   * ⚠️ SANS CE RECHARGEMENT, l'écran montrerait deux ADMIN : le nouveau, et
   * l'ancien qu'on vient de rétrograder. *Un écran qui ne relit pas après une
   * écriture de masse affiche l'état d'avant en annonçant celui d'après.*
   */
  const handleDesignerAdmin = async () => {
    if (!designation || !detail) return;
    setDesignationEnCours(true);
    try {
      const r = await designerAdmin(detail.id, designation.id);
      setDesignation(null);
      // On redit le nom que le SERVEUR rend, jamais celui qu'on avait annoncé.
      showSnackbar(
        r?.ancien_admin
          ? t('superAdmin.designateDone', { name: designation.full_name })
          : t('superAdmin.designateDoneFirst', { name: designation.full_name }),
        'success',
      );
      // On relit par le MÊME chemin que l'ouverture : une seconde
      // écriture du chargement divergerait de la première.
      const frais = await getCompany(detail.id);
      setDetail(frais?.company || null);
    } catch (e) {
      setDesignation(null);
      showSnackbar(e?.response?.data?.error || t('superAdmin.designateError'), 'error');
    } finally {
      setDesignationEnCours(false);
    }
  };

  const handleImpersonate = async (user) => {
    try {
      const data = await impersonate(user.id);
      // Les cookies httpOnly sont posés par le serveur : rien à stocker côté token.
      // localStorage ne sert qu'à peupler l'UI au rechargement.
      localStorage.setItem('user', JSON.stringify(data.user));
      localStorage.setItem('stocks', JSON.stringify(data.stocks));
      localStorage.setItem('impersonated', 'true');
      if (data.stocks && data.stocks.length > 0) {
        localStorage.setItem('currentStock', JSON.stringify(data.stocks[0]));
      }
      window.location.reload();
    } catch (err) {
      showSnackbar(
        err?.response?.data?.error || err?.message || t('superAdmin.impersonationError'),
        'error',
      );
    }
  };

  /**
   * Aide du champ override. La valeur héritée n'est PAS recalculée depuis une copie
   * front de PLAN_LIMITS — elle est lue dans la réponse serveur : quand l'override est
   * null, `max_stocks` EST la limite du plan. Dupliquer ces valeurs ferait mentir
   * l'aide en silence si le serveur les changeait.
   * Si le plan vient d'être modifié dans le formulaire, la valeur chargée ne correspond
   * plus au plan sélectionné : on bascule alors sur un message générique plutôt que
   * d'afficher un chiffre faux.
   */
  const overrideHelper = (effectiveValue) => {
    if (!editingCompany || formData.plan !== editingCompany.plan) {
      return t('superAdmin.overrideHint');
    }
    return t('superAdmin.overrideInherited', { value: effectiveValue });
  };

  const renderStatus = (status) => (
    <Chip
      size="small"
      label={status === 'suspended' ? t('superAdmin.statusSuspended') : t('superAdmin.statusActive')}
      color={status === 'suspended' ? 'error' : 'success'}
      variant={status === 'suspended' ? 'filled' : 'outlined'}
    />
  );

  const renderStockQuota = (company) => {
    const quota = stockQuotaFromCompanyRow(company);
    if (!quota) {
      return '—';
    }
    return (
      <Chip
        size="small"
        label={t('superAdmin.quotaCell', { current: quota.current, limit: quota.limit })}
        color={quota.reached ? 'warning' : 'default'}
        variant={quota.reached ? 'filled' : 'outlined'}
        title={quota.reached ? t('superAdmin.quotaSaturated') : undefined}
      />
    );
  };

  return (
    <Box>
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tab} onChange={handleTabChange}>
          <Tab label={t('superAdmin.title')} />
          <Tab label={t('superAdmin.auditTitle')} />
        </Tabs>
      </Paper>

      {tab === 0 && (
      <>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        {/* 🔴 `EnteteEcran` ICI AUSSI. Le châssis de la console porte son
            dégradé de MARQUE (`SuperAdminShell`) ; la page, elle, est une
            surface de TRAVAIL — c'est la distinction posée au lot RU2. */}
        <EnteteEcran titre={detail
          ? t('superAdmin.usersOf', { name: detail.name })
          : t('superAdmin.title')} />
        {detail ? (
          <Button variant="outlined" onClick={() => setDetail(null)}>
            {t('common.back')}
          </Button>
        ) : (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleDialogOpen()}>
            {t('superAdmin.newCompany')}
          </Button>
        )}
      </Box>

      {detailLoading && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="240px">
          <CircularProgress />
        </Box>
      )}

      {/* ─── VUE DE DÉTAIL : les utilisateurs de l'entreprise ─────────────────── */}
      {!detailLoading && detail && (
        detail.users.length === 0 ? (
          <Alert severity="info">{t('superAdmin.noUsers')}</Alert>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('common.name')}</TableCell>
                  <TableCell>{t('superAdmin.colEmail')}</TableCell>
                  <TableCell>{t('superAdmin.colRole')}</TableCell>
                  <TableCell>{t('common.status')}</TableCell>
                  <TableCell>{t('common.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {detail.users.map((u) => (
                  <TableRow key={u.id}>
                    <TableCell>{u.full_name}</TableCell>
                    <TableCell>{u.email}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={u.role === 'ADMIN' ? t('admin.roleAdmin') : t('admin.roleUser')}
                        color={u.role === 'ADMIN' ? 'error' : 'primary'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={u.is_active ? t('common.active') : t('common.inactive')}
                        color={u.is_active ? 'success' : 'default'}
                        variant={u.is_active ? 'outlined' : 'filled'}
                      />
                    </TableCell>
                    <TableCell>
                      {/*
                        Bouton MASQUÉ (pas désactivé) sur un compte inactif : le serveur
                        refuse la cible inactive en 400 (login_as_user), autant ne pas
                        proposer l'impossible. C'est bien D-UI-7 ici — l'action ne sera
                        jamais possible tant que le compte est désactivé, contrairement
                        au bouton Supprimer d'une entreprise non vide, dont la
                        précondition est transitoire et dont le refus est informatif.
                      */}
                      {u.is_active && (
                        <IconButton
                          color="primary"
                          onClick={() => setAConfirmer({ type: 'impersonate', entite: u })}
                          title={t('superAdmin.impersonate')}
                        >
                          <ImpersonateIcon />
                        </IconButton>
                      )}
                      {/* 🔴 « DÉSIGNER COMME ADMINISTRATEUR » — LE SEUL CHEMIN DE
                          TRANSFERT DU PRODUIT. Une société a un seul ADMIN, et
                          lui seul peut le désigner : *il est celui qui a créé la
                          société.* Le sélecteur de rôle a été retiré de l'écran
                          Admin au même lot.
                          ⚠️ MASQUÉ SUR L'ADMIN EN PLACE ET SUR UN COMPTE INACTIF
                          — deux cas où le serveur refuse (409 « déjà admin »,
                          400 « compte désactivé »). *On ne propose pas
                          l'impossible*, même quand le refus serait propre. */}
                      {u.is_active && u.role !== 'ADMIN' && (
                        <IconButton
                          color="warning"
                          onClick={() => setDesignation(u)}
                          title={t('superAdmin.designateAdmin')}
                        >
                          <AdminIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )
      )}

      {!detail && loading && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="240px">
          <CircularProgress />
        </Box>
      )}

      {!detail && !loading && error && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" startIcon={<RefreshIcon />} onClick={handleRetry}>
              {t('superAdmin.retry')}
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {!detail && !loading && !error && companies.length === 0 && (
        <Alert severity="info">{t('superAdmin.empty')}</Alert>
      )}

      {!detail && !loading && !error && companies.length > 0 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('superAdmin.colName')}</TableCell>
                <TableCell>{t('superAdmin.colPlan')}</TableCell>
                <TableCell>{t('superAdmin.colStatus')}</TableCell>
                <TableCell>{t('superAdmin.colStocks')}</TableCell>
                <TableCell>{t('superAdmin.colUsers')}</TableCell>
                <TableCell>{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {companies.map((company) => (
                <TableRow key={company.id}>
                  <TableCell>
                    {company.name}
                    {/*
                      Une entreprise sans ADMIN est INUTILISABLE : personne ne peut y
                      administrer stocks et utilisateurs. Le POST n'en crée aucun,
                      l'anomalie est donc produite par cette page même — elle doit s'y
                      voir. Le test est strict (=== 0) et non `!company.nb_admins` :
                      une clé absente d'une réponse plus ancienne ne doit pas déclencher
                      une fausse alerte.
                    */}
                    {company.nb_admins === 0 && (
                      <Tooltip title={t('superAdmin.noAdminHint')}>
                        <Chip
                          size="small"
                          icon={<WarningIcon />}
                          label={t('superAdmin.noAdmin')}
                          color="warning"
                          variant="outlined"
                          sx={{ ml: 1 }}
                        />
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={company.plan} variant="outlined" />
                  </TableCell>
                  <TableCell>{renderStatus(company.status)}</TableCell>
                  <TableCell>{renderStockQuota(company)}</TableCell>
                  {/*
                    nb_users est une MÉTRIQUE, jamais un quota : il compte TOUS les
                    utilisateurs de l'entreprise (ADMIN inclus), alors que le cap
                    users_per_stock s'applique PAR STOCK et ne compte que les
                    role == 'USER'. L'afficher en « x / y » serait faux.
                  */}
                  <TableCell>
                    <Tooltip title={t('superAdmin.usersHint')}>
                      <Typography variant="body2" component="span">
                        {company.nb_users}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <IconButton
                      color="default"
                      onClick={() => handleViewUsers(company)}
                      title={t('superAdmin.viewUsers')}
                    >
                      <UsersIcon />
                    </IconButton>
                    <IconButton
                      color="primary"
                      onClick={() => handleInvite(company)}
                      title={t('superAdmin.invite')}
                    >
                      <MailIcon />
                    </IconButton>
                    <IconButton
                      color="primary"
                      onClick={() => handleDialogOpen(company)}
                      title={t('common.edit')}
                    >
                      <EditIcon />
                    </IconButton>
                    {/*
                      Bouton TOUJOURS affiché, même quand l'entreprise n'est pas vide.
                      D-UI-7 (« masquer plutôt que désactiver ») vise l'AUTORISATION —
                      une action jamais possible pour ce rôle. Ici c'est une
                      PRÉCONDITION D'ÉTAT, transitoire : videz l'entreprise et l'action
                      redevient possible. Masquer priverait le SA du chemin qui lui
                      EXPLIQUE le refus et lui propose la suspension, et obligerait le
                      front à rejouer la règle du serveur (nb_stocks || nb_users) — une
                      duplication qui dériverait en silence.
                    */}
                    <IconButton
                      color="error"
                      onClick={() => ouvrirSuppression(company)}
                      title={t('common.delete')}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      </>
      )}

      {/* ─── ONGLET JOURNAL D'IMPERSONATION ──────────────────────────────────── */}
      {tab === 1 && (
        <>
          {logsLoading && (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="240px">
              <CircularProgress />
            </Box>
          )}

          {!logsLoading && logsError && (
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" startIcon={<RefreshIcon />} onClick={loadLogs}>
                  {t('superAdmin.retry')}
                </Button>
              }
            >
              {logsError}
            </Alert>
          )}

          {!logsLoading && !logsError && logs.length === 0 && (
            <Alert severity="info">{t('superAdmin.auditEmpty')}</Alert>
          )}

          {!logsLoading && !logsError && logs.length > 0 && (
            <>
              {/*
                ⚠️ NE PAS laisser croire que ces entrées sont des sessions ACTIVES.
                `ended_at` est toujours null : la colonne existe mais rien ne l'écrit.
                Une case vide se lirait « encore en cours » (faux — elles sont toutes
                terminées) ou « donnée manquante » (faux aussi — c'est par conception).
                D'où cette explication, et un libellé « non tracée » plutôt qu'« en cours ».
                Conditionnée : elle disparaîtra d'elle-même le jour où ended_at sera
                renseigné, sans qu'on ait à y penser.
              */}
              {logs.some((l) => !l.ended_at) && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {t('superAdmin.auditEndNotTrackedNotice')}
                </Alert>
              )}

              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {t('superAdmin.auditTotal', { total: logsTotal })}
              </Typography>

              {/* Pas de pagination dans ce lot : per_page=100. Au-delà, on le DIT plutôt
                  que de tronquer en silence. */}
              {logsTotal > logs.length && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {t('superAdmin.auditTruncated', { shown: logs.length, total: logsTotal })}
                </Alert>
              )}

              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('superAdmin.auditWhen')}</TableCell>
                      <TableCell>{t('superAdmin.auditWho')}</TableCell>
                      <TableCell>{t('superAdmin.auditTarget')}</TableCell>
                      <TableCell>{t('superAdmin.auditCompany')}</TableCell>
                      <TableCell>{t('superAdmin.auditEnd')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {logs.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell>
                          {log.created_at
                            ? formaterHorodatage(log.created_at)
                            : '—'}
                        </TableCell>
                        {/* Emails et nom d'entreprise sont RÉSOLUS côté serveur, et valent
                            null si l'entité a été supprimée depuis. */}
                        <TableCell>{log.super_admin_email || t('superAdmin.auditDeletedEntity')}</TableCell>
                        <TableCell>{log.target_user_email || t('superAdmin.auditDeletedEntity')}</TableCell>
                        <TableCell>{log.target_company_name || t('superAdmin.auditDeletedEntity')}</TableCell>
                        <TableCell>
                          {log.ended_at ? (
                            formaterHorodatage(log.ended_at)
                          ) : (
                            <Chip size="small" label={t('superAdmin.auditEndNotTracked')} variant="outlined" />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingCompany ? t('superAdmin.editCompany') : t('superAdmin.newCompany')}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label={t('superAdmin.fieldName')}
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            margin="normal"
            required
          />

          <FormControl fullWidth margin="normal">
            <InputLabel>{t('superAdmin.fieldPlan')}</InputLabel>
            <Select
              value={formData.plan}
              onChange={(e) => setFormData({ ...formData, plan: e.target.value })}
              label={t('superAdmin.fieldPlan')}
            >
              {SELECTABLE_PLANS.map((plan) => (
                <MenuItem key={plan} value={plan}>
                  {plan}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/*
            Le statut n'est modifiable QU'EN ÉDITION : le POST ne lit pas la clé et force
            'active' (companies.py:113). Champ affiché mais désactivé en création, avec
            son aide — le masquer laisserait l'utilisateur ignorer ce qui va se passer.
          */}
          <FormControl fullWidth margin="normal" disabled={!editingCompany}>
            <InputLabel>{t('superAdmin.fieldStatus')}</InputLabel>
            <Select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              label={t('superAdmin.fieldStatus')}
            >
              <MenuItem value="active">{t('superAdmin.statusActive')}</MenuItem>
              <MenuItem value="suspended">{t('superAdmin.statusSuspended')}</MenuItem>
            </Select>
            {!editingCompany && (
              <Typography variant="caption" sx={{ mt: 0.5, ml: 1.75, color: 'text.secondary' }}>
                {t('superAdmin.statusCreateHint')}
              </Typography>
            )}
          </FormControl>

          <TextField
            fullWidth
            type="number"
            label={t('superAdmin.fieldStocksOverride')}
            value={formData.maxStocks}
            onChange={(e) => setFormData({ ...formData, maxStocks: e.target.value })}
            margin="normal"
            helperText={overrideHelper(editingCompany?.max_stocks)}
            inputProps={{ min: 1 }}
          />

          <TextField
            fullWidth
            type="number"
            label={t('superAdmin.fieldUsersOverride')}
            value={formData.usersPerStock}
            onChange={(e) => setFormData({ ...formData, usersPerStock: e.target.value })}
            margin="normal"
            helperText={overrideHelper(editingCompany?.users_per_stock)}
            inputProps={{ min: 1 }}
          />

          {/* RACCOURCI, et uniquement en CRÉATION : inviter à la modification
              n'aurait pas de sens — le bouton « Inviter » de la ligne le fait
              déjà, et c'est LUI le chemin principal. Facultatif : créer une
              entreprise sans inviter reste possible, et c'est le cas nominal
              quand on ne connaît pas encore l'adresse de l'administrateur. */}
          {!editingCompany && (
            <TextField
              fullWidth
              type="email"
              label={t('superAdmin.adminEmail')}
              value={formData.adminEmail}
              onChange={(e) => setFormData({ ...formData, adminEmail: e.target.value })}
              margin="normal"
              helperText={t('superAdmin.adminEmailHelp')}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={saving}>
            {editingCompany ? t('common.edit') : t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/*
        Refus de suppression (409). Le message serveur est affiché BRUT : il porte les
        compteurs réels et suggère déjà la suspension. Deux issues, parce qu'un refus
        sans alternative serait une impasse.
      */}
      <Dialog open={Boolean(deleteBlocked)} onClose={() => setDeleteBlocked(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('superAdmin.deleteBlockedTitle')}</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mt: 1 }}>
            {deleteBlocked?.message}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteBlocked(null)}>{t('common.cancel')}</Button>
          <Button
            onClick={() => { const c = deleteBlocked.company;
                             setDeleteBlocked(null);
                             setAConfirmer({ type: 'suspend', entite: c }); }}
            variant="contained"
            color="warning"
            startIcon={<SuspendIcon />}
          >
            {t('superAdmin.suspendInstead')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 🔴 UNE ANNONCE POUR LES TROIS GESTES LOURDS DE CET ÉCRAN. Chacune dit
          CE QUE LE GESTE EMPORTE, avec les chiffres qu'on a :
            · SUPPRIMER  — les sites et les utilisateurs de l'entreprise ;
            · SUSPENDRE  — *tous les logins sont coupés*, conséquence plus
              lourde que la suppression qu'on vient de refuser ;
            · IMPERSONER — on agit SOUS L'IDENTITÉ de quelqu'un, et c'est tracé.
          ⚠️ Les compteurs viennent de la LISTE, qui les sert (`nb_stocks`,
          `nb_users`) : on n'annonce rien qu'on ne mesure pas. */}
      <DialogueConfirmation
        ouvert={Boolean(aConfirmer)}
        titre={t(`superAdmin.${aConfirmer?.type || 'delete'}Title`)}
        question={t(`superAdmin.${aConfirmer?.type || 'delete'}Question`, {
          name: aConfirmer?.entite?.name || aConfirmer?.entite?.full_name || '',
        })}
        consequences={aConfirmer?.type === 'impersonate' ? null
          : t('superAdmin.companyCarries', {
              stocks: aConfirmer?.entite?.nb_stocks ?? 0,
              users: aConfirmer?.entite?.nb_users ?? 0,
            })}
        avertissement={t(`superAdmin.${aConfirmer?.type || 'delete'}Warning`)}
        couleur={aConfirmer?.type === 'impersonate' ? 'primary' : 'error'}
        enCours={confirmationEnCours}
        onConfirmer={async () => {
          const { type, entite } = aConfirmer;
          setConfirmationEnCours(true);
          setAConfirmer(null);
          try {
            // ⚠️ `delete` NE PASSE PLUS PAR ICI — il a son propre dialogue,
            // parce qu'il porte une SAISIE. Laisser la branche aurait créé un
            // second chemin de suppression, sans confirmation par le nom.
            if (type === 'suspend') await handleSuspend(entite);
            else await handleImpersonate(entite);
          } finally {
            setConfirmationEnCours(false);
          }
        }}
        onFermer={() => setAConfirmer(null)}
      />

      {/* 🔴 LA SUPPRESSION A SON PROPRE DIALOGUE — il porte une SAISIE, et une
          saisie change la nature de la confirmation : on ne demande plus
          « êtes-vous sûr », on demande de DÉSIGNER la cible. */}
      <DialogueSuppressionEntreprise
        ouvert={Boolean(suppression)}
        impact={suppression?.impact}
        chargement={impactEnCours}
        enCours={confirmationEnCours}
        erreur={suppression?.erreur}
        onConfirmer={handleDelete}
        onFermer={() => setSuppression(null)}
      />

      {/* 🔴 L'ANNONCE AVANT LE TRANSFERT — et elle dit QUI PERD SA PLACE. Une
          confirmation qui demande seulement « êtes-vous sûr » n'apprend rien ;
          celle-ci nomme les DEUX personnes que le geste touche, et dit que
          l'ancien devient un utilisateur ordinaire.
          ⚠️ Et elle distingue les deux cas : sans ADMIN en place, il n'y a
          personne à rétrograder, et le dialogue ne doit pas inventer une perte
          qui n'existe pas. */}
      <Dialog open={Boolean(designation)} onClose={() => setDesignation(null)}
              maxWidth="xs" fullWidth>
        <DialogTitle>{t('superAdmin.designateTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t('superAdmin.designateSummary', { name: designation?.full_name || '' })}
          </Typography>
          {(detail?.users || []).some((x) => x.role === 'ADMIN') && (
            <Typography variant="body2" sx={{ mt: 1.5, color: 'warning.main' }}>
              {t('superAdmin.designateReplaces', {
                name: (detail.users.find((x) => x.role === 'ADMIN') || {}).full_name || '',
              })}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setDesignation(null)}>
            {t('common.cancel')}
          </Button>
          <Button variant="contained" onClick={handleDesignerAdmin}
                  disabled={designationEnCours}>
            {t('superAdmin.designateConfirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 🔴 UN VRAI FORMULAIRE À LA PLACE DU `window.prompt` — il valide le
          FORMAT avant d'envoyer, il montre son état de chargement, et il suit
          le thème et la langue. *Le prompt système ne pouvait faire aucune des
          trois choses.* */}
      <Dialog open={Boolean(invitation)} onClose={() => setInvitation(null)}
              maxWidth="xs" fullWidth>
        <DialogTitle>{t('superAdmin.inviteTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('superAdmin.inviteQuestion', { nom: invitation?.company?.name || '' })}
          </Typography>
          <TextField
            autoFocus fullWidth type="email" margin="dense"
            label={t('admin.email')}
            value={invitation?.email || ''}
            onChange={(e) => setInvitation((v) => ({ ...v, email: e.target.value }))}
            disabled={invitationEnCours}
            /* ⚠️ LA VALIDATION EST ICI PARCE QU'ELLE PEUT L'ÊTRE : le prompt
               système rendait une chaîne sans jamais pouvoir la refuser. */
            error={Boolean(invitation?.email)
              && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(invitation.email.trim())}
            helperText={t('superAdmin.inviteHelp')}
            onKeyDown={(e) => { if (e.key === 'Enter') envoyerInvitation(); }}
          />
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setInvitation(null)}
                  disabled={invitationEnCours}>
            {t('common.cancel')}
          </Button>
          <Button variant="contained" onClick={envoyerInvitation}
                  disabled={invitationEnCours
                    || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test((invitation?.email || '').trim())}>
            {invitationEnCours ? t('common.loading') : t('superAdmin.invite')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SuperAdminConsole;
